import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import {
  buildCountryListParams,
  deleteCountryMaster,
  getCountriesMaster,
  getCountryMasterById,
} from "../../api/superAdminApi";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import AddCountryModal from "./CountryMaster/AddCountryModal";
import GeoMasterActionCell from "./GeoMasterActionCell";
import GeoMasterDeleteDialog from "./GeoMasterDeleteDialog";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 25, 50];
const TABLE_HEADERS = ["S.No", "Country", "Country code", "Currency", "Symbol", "Actions"];

export default function VMSuperAdminCountryMaster() {
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filtersRef = useRef({ appliedSearch, page, rowsPerPage });
  filtersRef.current = { appliedSearch, page, rowsPerPage };

  const fetchList = useCallback(async (overrides = {}) => {
    const f = { ...filtersRef.current, ...overrides };
    setLoading(true);
    try {
      const params = buildCountryListParams({
        page: f.page,
        pageSize: f.rowsPerPage,
        search: f.appliedSearch,
      });
      const res = await getCountriesMaster(params);
      const body = res.data;
      if (body?.status_code && body.status_code !== 200) {
        throw new Error(body.message || "Failed to load countries");
      }
      setRows(Array.isArray(body?.data) ? body.data : []);
      setTotalRecords(Number(body?.totalCount) || 0);
      if (typeof body?.page === "number") setPage(body.page);
    } catch (error) {
      setRows([]);
      setTotalRecords(0);
      toast.error(error?.response?.data?.message || error?.message || "Country master API unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const nextSearch = searchInput.trim();
    setAppliedSearch(nextSearch);
    setPage(0);
    fetchList({ appliedSearch: nextSearch, page: 0 });
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchList({ page: newPage });
  };

  const handleRowsPerPageChange = (e) => {
    const next = parseInt(e.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    fetchList({ rowsPerPage: next, page: 0 });
  };

  const openAdd = () => {
    setEditingRow(null);
    setFormOpen(true);
  };

  const openEdit = async (row) => {
    if (!row?.cid) return;
    setEditLoading(true);
    try {
      const res = await getCountryMasterById(row.cid);
      const body = res.data;
      if (body?.status_code === 200 && body?.data) {
        setEditingRow(body.data);
        setFormOpen(true);
      } else {
        toast.error(body?.message || "Failed to load country.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load country.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow?.cid) return;
    setDeleting(true);
    try {
      const res = await deleteCountryMaster(deleteRow.cid);
      const body = res.data;
      if (body?.status_code === 200) {
        const statesDeleted = body?.data?.states_deleted ?? 0;
        const citiesDeleted = body?.data?.cities_deleted ?? 0;
        toast.success(
          body.message ||
            `Country deleted (${statesDeleted} states, ${citiesDeleted} cities soft-deleted).`
        );
        setDeleteRow(null);
        fetchList();
      } else {
        toast.error(body?.message || "Failed to delete country.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete country.");
    } finally {
      setDeleting(false);
    }
  };

  const from = totalRecords === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, totalRecords);

  return (
    <Box>
      <SuperAdminPageHeader
        title="Country Master"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add country
          </Button>
        }
      />

      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          label="Search country"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          sx={{ minWidth: 260 }}
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading}>
          Search
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Total countries (filtered): {loading ? "…" : totalRecords.toLocaleString()}
        {!loading && totalRecords > 0 ? ` · Showing ${from} to ${to}` : ""}
        {editLoading ? " · Loading country…" : ""}
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {TABLE_HEADERS.map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No countries found for the current criteria.
                </TableCell>
              </TableRow>
            ) : loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row.cid} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{row.country_name || "—"}</TableCell>
                  <TableCell>{row.country_code || "—"}</TableCell>
                  <TableCell>{row.currency_code || "—"}</TableCell>
                  <TableCell>{row.currency_symbol || "—"}</TableCell>
                  <TableCell>
                    <GeoMasterActionCell
                      onEdit={() => openEdit(row)}
                      onDelete={() => setDeleteRow(row)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <AddCountryModal
        open={formOpen}
        initialData={editingRow}
        onClose={() => {
          setFormOpen(false);
          setEditingRow(null);
        }}
        onSuccess={() => fetchList()}
      />

      <GeoMasterDeleteDialog
        open={Boolean(deleteRow)}
        title="Delete country"
        message={`Delete "${deleteRow?.country_name || "this country"}"? Related states and cities will also be soft-deleted and hidden from lists.`}
        loading={deleting}
        onClose={() => !deleting && setDeleteRow(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
